<template>
  <div id="app" :class="{ 'dark-mode': isDarkMode }">
    <!-- Authentication Pages (Full Screen with Sliding Animation) -->
    <template v-if="isAuthPage">
      <Auth
        :mode="currentPage"                      
        @loginSuccess="handleAuthSuccess"
        @signUpSuccess="handleAuthSuccess"
        @backToHome="setCurrentPage('Home')"
        @switchToSignUp="setCurrentPage('SignUp')" />
    </template>

    <!-- Main App Layout -->
    <template v-else>
      <!-- Top Bar -->
      <div class="top-bar">
        <div class="top-bar-container">
          <div class="contact-info">
            <div class="contact-item">
              <span class="contact-icon">📞</span>
              <span class="contact-text">0946 102 0314</span>
            </div>
            <div class="contact-item">
              <span class="contact-icon">✉️</span>
              <span class="contact-text">ramyeoncornerbislig@gmail.com</span>
            </div>
          </div>
          <div class="auth-social">
            <div class="auth-links" v-if="!isLoggedIn">
              <a href="#" class="auth-btn login-btn" @click.prevent="setCurrentPage('Login')">Login</a>
              <a href="#" class="auth-btn signup-btn" @click.prevent="setCurrentPage('SignUp')">Sign Up</a>
            </div>
            <div class="user-menu" v-else>
              <span class="welcome-text">Welcome, {{ currentUser.firstName }}!</span>
              <a href="#" class="profile-link" @click.prevent="setCurrentPage('Profile')">Profile</a>
              <a href="#" class="logout-link" @click.prevent="handleLogout">Sign Out</a>
            </div>
             <div class="social-icons">
              <a href="https://www.facebook.com/ramyeoncornerbislig" class="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
                <img src="./assets/Nav Bar/fb.png" alt="Facebook" />
              </a>
              <!--<a href="#" class="social-link" aria-label="Twitter">
                <img src="./assets/Nav Bar/twt.png" alt="Twitter" />
              </a>
              <a href="#" class="social-link" aria-label="Instagram">
                <img src="./assets/Nav Bar/ig.png" alt="Instagram" />
              </a>
              <a href="#" class="social-link" aria-label="Github">
                <img src="./assets/Nav Bar/git.png" alt="Github" />
              </a>-->
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation Bar -->
      <nav class="navbar">
        <div>
          <div class="logo">
            <img src="./assets/Nav Bar/Logo.png" alt="Ramyeon Corner Logo" class="logo-img" />
          </div>
          <ul class="nav-links">
            <li><a href="#" :class="{ active: currentPage === 'Home' }" @click.prevent="setCurrentPage('Home')">Home</a></li>
            <li><a href="#" :class="{ active: currentPage === 'About' }" @click.prevent="setCurrentPage('About')">About</a></li>
            <li><a href="#" :class="{ active: currentPage === 'Menu' }" @click.prevent="setCurrentPage('Menu')">Menu</a></li>
            <li><a href="#" :class="{ active: currentPage === 'Promotions' }" @click.prevent="setCurrentPage('Promotions')">Promotions</a></li>
            <li><a href="#" :class="{ active: currentPage === 'Contact' }" @click.prevent="setCurrentPage('Contact')">Contact Us</a></li>
          </ul>
          <button class="cart-btn" aria-label="Cart" @click="setCurrentPage('Cart')">
            🛒 <span class="cart-count">{{ cartCount }}</span>
          </button>
        </div>
      </nav>

      <!-- Active Order Banner (FoodPanda-style) -->
      <div v-if="activeOrder && !activeOrderMinimized" class="order-banner" :class="orderBannerClass">
        <div class="order-banner-inner">
          <div class="order-banner-left">
            <span class="order-pulse-dot" :class="orderBannerClass"></span>
            <div class="order-banner-info">
              <span class="order-banner-label">{{ orderStatusLabel }}</span>
              <span class="order-banner-id">#{{ activeOrder.id }}</span>
            </div>
          </div>
          <div class="order-banner-steps">
            <template v-for="(step, i) in orderSteps" :key="step.key">
              <div class="order-step" :class="{ completed: step.completed, active: step.active }">
                <div class="step-dot"></div>
                <span class="step-label">{{ step.label }}</span>
              </div>
              <div v-if="i < orderSteps.length - 1" class="step-connector" :class="{ completed: step.completed }"></div>
            </template>
          </div>
          <div class="order-banner-actions">
            <button class="order-view-btn" @click="setCurrentPage('OrderHistory')">View Details</button>
            <button class="order-banner-minimize" @click="activeOrderMinimized = true" title="Minimize">&#8212;</button>
            <button class="order-banner-close" @click="dismissOrderBanner" title="Dismiss">&#10005;</button>
          </div>
        </div>
      </div>
      <div v-else-if="activeOrder && activeOrderMinimized" class="order-banner-pill" @click="activeOrderMinimized = false">
        <span class="order-pulse-dot" :class="orderBannerClass"></span>
        <span>{{ orderStatusLabel }} &nbsp;·&nbsp; #{{ activeOrder.id }}</span>
        <span class="pill-expand">&#9650;</span>
      </div>

      <!-- Page Content -->
      <HelloWorld v-if="currentPage === 'Home'" @setCurrentPage="setCurrentPage" />
      <AboutUs v-if="currentPage === 'About'" />
      <MenuPage v-if="currentPage === 'Menu'" :onAddToCart="addToCart" />
      <ContactUs v-if="currentPage === 'Contact'" />
      <Promotions v-if="currentPage === 'Promotions'" @setCurrentPage="setCurrentPage" :isLoggedIn="isLoggedIn" />
      <Profile v-if="currentPage === 'Profile'" @setCurrentPage="setCurrentPage" />
      <ProfileSettings v-if="currentPage === 'ProfileSettings'" @setCurrentPage="setCurrentPage" />
      <Settings v-if="currentPage === 'Settings'" @setCurrentPage="setCurrentPage" />
      <Cart v-if="currentPage === 'Cart'" @setCurrentPage="setCurrentPage" @cartCleared="handleCartCleared" :key="cartKey" />
      <OrderHistory v-if="currentPage === 'OrderHistory'" @setCurrentPage="setCurrentPage" :key="orderHistoryKey" />
      <PaymentHistory v-if="currentPage === 'PaymentHistory'" @setCurrentPage="setCurrentPage" :key="paymentHistoryKey" />

      <!-- Sign Out Confirmation Modal -->
      <div v-if="showSignOutModal" class="signout-modal-overlay" @click="cancelSignOut">
        <div class="signout-modal" @click.stop>
          <div class="signout-modal-header">
            <h3>Sign Out</h3>
            <button class="signout-close-btn" @click="cancelSignOut">✕</button>
          </div>
          <div class="signout-modal-body">
            <div class="signout-icon">🚪</div>
            <p class="signout-message">Are you sure you want to sign out?</p>
            <p class="signout-submessage">You will be redirected to the home page.</p>
          </div>
          <div class="signout-modal-footer">
            <button class="signout-btn cancel-btn" @click="cancelSignOut">Cancel</button>
            <button class="signout-btn confirm-btn" @click="confirmSignOut">Sign Out</button>
          </div>
        </div>
      </div>

      <!-- Footer Section -->
      <footer class="footer" v-if="!isProfilePage">
        <div class="footer-content">
          <div class="footer-main">
            <!-- Brand Section -->
            <div class="footer-brand">
              <img src="./assets/Nav Bar/Logo.png" alt="Ramyeon Corner Logo" class="footer-logo" />
              <p class="footer-address">
                Purok I, Castillo, Mangagoy,<br>
                Bislig, Philippines
              </p>
              <p class="footer-contact">📞 0946 102 0314</p>
              <p class="footer-contact">✉️ ramyeoncornerbislig@gmail.com</p>
            </div>

            <!-- Newsletter Section -->
            <div class="footer-newsletter">
              <div class="newsletter-header">
                <div class="newsletter-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="22,6 12,13 2,6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <h3>Sign Up and get exclusive deals!</h3>
              </div>
              <!-- newsletter form commented out -->
            </div>
            
            <!-- Legal Pages -->
            <div class="footer-links">
              <!-- commented content -->
            </div>

            <!-- Important Links -->
            <div class="footer-links">
              <!-- commented content -->
            </div>
        </div>
      </div>

      <!-- Social Media Icons (Outside Bottom Footer) -->
      <div class="footer-social">
        <a href="https://www.facebook.com/ramyeoncornerbislig" class="social-link" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
          <img src="./assets/Nav Bar/fb.png" alt="Facebook" />
        </a>
      </div>

      <!-- Copyright Section -->
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <p>&copy; 2025 Ramyeon Corner. All rights reserved.</p>
        </div>
      </div>
      </footer>
    </template>
  </div>
</template>


<script>
import { ordersAPI } from './services/api.js'
import HelloWorld from './components/HelloWorld.vue'
import AboutUs from './components/AboutUs.vue'
import MenuPage from './components/Menu.vue'
import ContactUs from './components/ContactUs.vue'
import Promotions from './components/Promotions.vue'
import Auth from './components/Auth.vue'
import Profile from './components/Profile.vue'
import ProfileSettings from './components/ProfileSettings.vue'
import Settings from './components/Settings.vue'
import Cart from './components/Cart.vue'
import OrderHistory from './components/OrderHistory.vue'
import PaymentHistory from './components/PaymentHistory.vue'

const AUTH_REQUIRED_PAGES = new Set([
  'Cart',
  'Profile',
  'ProfileSettings',
  'Settings',
  'OrderHistory',
  'PaymentHistory'
])

export default {
  name: 'App',
  components: {
    HelloWorld,
    AboutUs,
    MenuPage,
    ContactUs,
    Promotions,
    Auth,
    Profile,
    ProfileSettings,
    Settings,
    Cart,
    OrderHistory,
    PaymentHistory
  },
  data() {
    return {
      currentPage: 'Home',
      cartItems: [],
      newsletterEmail: '',
      currentUser: null,
      isDarkMode: false,
      subscriptionError: '',
      subscriptionSuccess: '',
      isSubscribing: false,
      showSignOutModal: false,
      cartKey: 0,
      orderHistoryKey: 0,
      paymentHistoryKey: 0,
      activeOrder: null,
      activeOrderMinimized: false
    }
  },
  computed: {
    isLoggedIn() {
      return this.currentUser !== null;
    },
    isAuthPage() {
      return ['Login', 'SignUp'].includes(this.currentPage);
    },
    isProfilePage() {
      return ['Profile', 'ProfileSettings', 'Settings', 'Cart'].includes(this.currentPage);
    },
    cartCount() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0);
    },
    orderStatusLabel() {
      const map = {
        pending: 'Order Received',
        confirmed: 'Order Confirmed',
        processing: 'Preparing',
        preparing: 'Preparing',
        cooking: 'Cooking',
        ready: 'Ready!',
        shipped: 'On the Way',
        on_the_way: 'On the Way',
        out_for_delivery: 'On the Way',
        delivered: 'Delivered!',
        completed: 'Delivered!',
        cancelled: 'Cancelled',
      };
      return map[this.activeOrder?.status] || 'Processing';
    },
    orderBannerClass() {
      const s = this.activeOrder?.status;
      if (s === 'cancelled') return 'banner-cancelled';
      if (s === 'delivered' || s === 'completed') return 'banner-done';
      if (s === 'shipped' || s === 'on_the_way' || s === 'out_for_delivery') return 'banner-otw';
      if (s === 'processing' || s === 'preparing' || s === 'cooking' || s === 'ready') return 'banner-preparing';
      return 'banner-active';
    },
    orderSteps() {
      const s = this.activeOrder?.status || 'pending';
      // Normalize backend statuses to the step progression
      const normalize = {
        preparing: 'processing', cooking: 'processing', ready: 'processing',
        shipped: 'on_the_way', out_for_delivery: 'on_the_way',
        completed: 'delivered',
      };
      const normalized = normalize[s] || s;
      const ORDER = ['pending', 'confirmed', 'processing', 'on_the_way', 'delivered'];
      const currentIdx = ORDER.indexOf(normalized);
      return [
        { key: 'pending',    label: 'Received' },
        { key: 'confirmed',  label: 'Confirmed' },
        { key: 'processing', label: 'Preparing' },
        { key: 'on_the_way', label: 'On the Way' },
        { key: 'delivered',  label: 'Delivered' },
      ].map((step, i) => ({
        ...step,
        completed: i < currentIdx,
        active: i === currentIdx,
      }));
    },
  },
  mounted() {
    this.checkURLHash();
    window.addEventListener('cart-updated', this.loadCartItems);
    window.addEventListener('order-placed', this.handleOrderPlaced);
    this.checkUserSession();
    this.loadDarkModePreference();
    this.loadCartItems();
    this.loadActiveOrder();
    window.addEventListener('hashchange', this.handleHashChange);
  },
  beforeUnmount() {
    window.removeEventListener('cart-updated', this.loadCartItems);
    window.removeEventListener('order-placed', this.handleOrderPlaced);
    clearInterval(this._orderPollTimer);
  },
  methods: {
    resetCart() {
      // Remove all cart items from app state
      this.cartItems = [];

      // Remove cart from localStorage
      localStorage.removeItem('ramyeon_cart');

      // Force re-render (Very important for UI badge update)
      this.cartKey++;

      // Optional: ensure UI refreshes
      this.$nextTick(() => this.$forceUpdate());
    },

    handleHashChange() {
      this.checkURLHash();   // Re-read URL and update currentPage
      this.scheduleScrollReset();
    },

    checkURLHash() {
      const rawHash = window.location.hash || '';

      if (!rawHash || rawHash === '#/' || rawHash === '#') {
        this.currentPage = 'Home';
        return;
      }

      const trimmedHash = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
      const withoutLeadingSlash = trimmedHash.startsWith('/') ? trimmedHash.slice(1) : trimmedHash;

      // Some providers append additional fragments (#/oauth?#/something). Only look at the first segment.
      const [primarySegment] = withoutLeadingSlash.split('#', 1);
      const [routeSegmentRaw, queryString = ''] = primarySegment.split('?');
      const routeSegment = (routeSegmentRaw || '').toLowerCase();

      if (!routeSegment) {
        this.currentPage = 'Home';
        return;
      }

      if (routeSegment === 'oauth') {
        this.processOAuthCallback(queryString);
        return;
      }

      const pageMap = {
        'cart': 'Cart',
        'menu': 'Menu',
        'about': 'About',
        'promotions': 'Promotions',
        'contact': 'Contact',
        'profile': 'Profile',
        'login': 'Login',
        'signup': 'SignUp',
        'order-history': 'OrderHistory',
        'payment-history': 'PaymentHistory',
        'settings': 'Settings',
        'profile-settings': 'ProfileSettings'
      };

      const mappedPage = pageMap[routeSegment];
      this.currentPage = mappedPage || 'Home';
    },

    processOAuthCallback(queryString) {
      const params = new URLSearchParams(queryString || '');
      const status = params.get('status');

      if (!status) {
        this.currentPage = 'Home';
        return;
      }

      if (status === 'success') {
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const userId = params.get('user_id') || '';
        const email = params.get('user_email') || '';
        const fullName = params.get('user_name') || '';
        const username = params.get('username') || (email ? email.split('@')[0] : 'guest');
        const emailVerified = params.get('email_verified') === '1';

        if (accessToken) {
          localStorage.setItem('access_token', accessToken);
        }
        if (refreshToken) {
          localStorage.setItem('refresh_token', refreshToken);
        }

        const firstName = fullName ? fullName.split(' ')[0] : username;
        const lastName = fullName ? fullName.split(' ').slice(1).join(' ') : '';

        const userSession = {
          id: userId,
          email,
          username,
          fullName: fullName || username,
          firstName,
          lastName,
          phone: '',
          points: 0,
          deliveryAddress: {},
          emailVerified,
          authMode: 'oauth',
          loginTime: new Date().toISOString()
        };

        localStorage.setItem('ramyeon_user_session', JSON.stringify(userSession));
        this.currentUser = userSession;
        this.setCurrentPage('Profile');
        window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}#/profile`);
        return;
      }

      const message = params.get('message') || 'OAuth login failed. Please try again.';
      alert(message);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.history.replaceState({}, document.title, `${window.location.origin}${window.location.pathname}#/login`);
      this.currentPage = 'Login';
    },
    
   setCurrentPage(page, context = {}) {
      const requestedPage = page;

      // Handle login redirection intent (e.g., from Order Now CTA)
      if (context.from === 'OrderNow') {
        localStorage.setItem('loginRedirect', 'Menu');
      }

      if (!this.isLoggedIn && this.requiresAuthentication(requestedPage)) {
        if (context.from !== 'OrderNow') {
          localStorage.setItem('loginRedirect', requestedPage);
        }
        this.showLoginRequiredToast(this.getLoginRequiredMessage(requestedPage));
        page = 'Login';
      }

      this.currentPage = page;

      // ✅ Update URL hash when page changes
      const hashMap = {
        'Home': '',
        'Cart': 'cart',
        'Menu': 'menu',
        'About': 'about',
        'Promotions': 'promotions',
        'Contact': 'contact',
        'Profile': 'profile',
        'Login': 'login',
        'SignUp': 'signup',
        'OrderHistory': 'order-history',
        'PaymentHistory': 'payment-history',
        'Settings': 'settings',
        'ProfileSettings': 'profile-settings'
      };

      const hashName = hashMap[page];
      if (hashName !== undefined) {
        // Don't update hash if we're already on a page with query params (like payment returns)
        if (!window.location.hash.includes('?')) {
          window.location.hash = hashName ? `#/${hashName}` : '#/';
        }
      }

      // ✅ Force reload of certain components when navigating to them
      if (page === 'OrderHistory') {
        this.orderHistoryKey++;
      } else if (page === 'PaymentHistory') {
        this.paymentHistoryKey++;
      } else if (page === 'Cart') {
        this.cartKey++;
        // Reload cart items when navigating to cart page to ensure sync
        this.loadCartItems();
      }

      this.scheduleScrollReset();
    },

    scheduleScrollReset() {
      if (typeof window === 'undefined') return;
      this.$nextTick(() => {
        const scrollOptions = { top: 0, behavior: 'smooth' };
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            try {
              window.scrollTo(scrollOptions);
            } catch (_) {
              window.scrollTo(0, 0);
            }
          });
        } else {
          try {
            window.scrollTo(scrollOptions);
          } catch (_) {
            window.scrollTo(0, 0);
          }
        }
      });
    },
    
    requiresAuthentication(page) {
      return AUTH_REQUIRED_PAGES.has(page);
    },

    getLoginRequiredMessage(page) {
      switch (page) {
        case 'Cart':
          return 'Please log in to view your cart and checkout.';
        case 'Profile':
        case 'ProfileSettings':
          return 'Please log in to view or update your profile.';
        case 'OrderHistory':
          return 'Please log in to review your past orders.';
        case 'PaymentHistory':
          return 'Please log in to view your payment history.';
        case 'Settings':
          return 'Please log in to update your settings.';
        default:
          return 'Please log in to continue.';
      }
    },

    showLoginRequiredToast(message = 'Please log in to continue.') {
      if (typeof window === 'undefined') return;

      const existingToast = document.getElementById('login-required-toast');
      if (existingToast) {
        existingToast.remove();
      }

      const toast = document.createElement('div');
      toast.id = 'login-required-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.style.cssText = [
        'position: fixed',
        'top: 20px',
        'right: 20px',
        'background: #1f2a37',
        'color: #ffffff',
        'padding: 14px 18px',
        'border-radius: 12px',
        'box-shadow: 0 12px 30px rgba(15, 23, 42, 0.25)',
        'font-family: "Poppins", sans-serif',
        'display: flex',
        'align-items: center',
        'gap: 12px',
        'z-index: 10000',
        'opacity: 0',
        'transform: translateY(-10px)',
        'transition: opacity 0.3s ease, transform 0.3s ease'
      ].join(';');

      const icon = document.createElement('span');
      icon.textContent = '🔒';
      icon.style.fontSize = '20px';

      const textWrapper = document.createElement('div');
      textWrapper.style.display = 'flex';
      textWrapper.style.flexDirection = 'column';
      textWrapper.style.gap = '2px';

      const title = document.createElement('strong');
      title.textContent = 'Login required';
      title.style.fontSize = '14px';

      const body = document.createElement('span');
      body.textContent = message;
      body.style.fontSize = '13px';
      body.style.opacity = '0.9';

      textWrapper.appendChild(title);
      textWrapper.appendChild(body);

      toast.appendChild(icon);
      toast.appendChild(textWrapper);

      document.body.appendChild(toast);

      const animateIn = () => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
      };

      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(animateIn);
      } else {
        animateIn();
      }

      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(-10px)';
        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, 300);
      }, 2800);
    },

    
    addToCart(product) {
      if (!this.isLoggedIn) {
        localStorage.setItem('loginRedirect', this.currentPage || 'Menu');
        this.showLoginRequiredToast('Please log in to add items to your cart.');
        this.setCurrentPage('Login');
        return;
      }

      const existingItem = this.cartItems.find(item => item.id === product.id);

      if (existingItem) {
        existingItem.quantity++;
      } else {
        this.cartItems.push({
          ...product,
          quantity: 1
        });
      }

      localStorage.setItem('ramyeon_cart', JSON.stringify(this.cartItems));

      // 👉 IMPORTANT: sync UI everywhere
      window.dispatchEvent(new Event('cart-updated'));

      this.showCartNotification(product.name);
    },
    
    showCartNotification(productName) {
      // Simple notification - in a real app you might use a toast library
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: #28a745;
          color: white;
          padding: 15px 20px;
          border-radius: 10px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
          z-index: 9999;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          animation: slideIn 0.3s ease-out;
        ">
          ✅ ${productName} added to cart!
        </div>
        <style>
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        </style>
      `;
      
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 2000);
    },
    
    async subscribe() {
      // Clear previous messages
      this.subscriptionError = '';
      this.subscriptionSuccess = '';
      
      if (!this.newsletterEmail) {
        this.subscriptionError = 'Please enter your email address';
        return;
      }
      if (!this.isValidEmail(this.newsletterEmail)) {
        this.subscriptionError = 'Please enter a valid email address';
        return;
      }
      
      this.isSubscribing = true;
      
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        this.subscriptionSuccess = `Successfully subscribed with ${this.newsletterEmail}!`;
        this.newsletterEmail = '';
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          this.subscriptionSuccess = '';
        }, 5000);
        
      } catch (error) {
        this.subscriptionError = 'Something went wrong. Please try again.';
      } finally {
        this.isSubscribing = false;
      }
    },
    
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    },
    
    checkUserSession() {
      const userSession = localStorage.getItem('ramyeon_user_session');
      if (userSession) {
        try {
          this.currentUser = JSON.parse(userSession);
        } catch (error) {
          console.error('Error parsing user session:', error);
          localStorage.removeItem('ramyeon_user_session');
        }
      }
    },
    
    handleAuthSuccess(userData) {
    this.currentUser = userData;
    const redirectTarget = localStorage.getItem('loginRedirect') || 'Profile';
    localStorage.removeItem('loginRedirect');
    this.setCurrentPage(redirectTarget);
  },

    
    handleLogout() {
      this.showSignOutModal = true;
    },

    confirmSignOut() {
      this.currentUser = null;
      localStorage.removeItem('ramyeon_user_session');
      this.resetCart();
      this.dismissOrderBanner();
      this.showSignOutModal = false;
      this.setCurrentPage('Home');

      // Optional success popup
      this.showSignOutSuccess();
    },


    cancelSignOut() {
      this.showSignOutModal = false;
    },

    showSignOutSuccess() {
      const notification = document.createElement('div');
      notification.innerHTML = `
        <div style="
          position: fixed;
          top: 20px;
          right: 20px;
          background: #28a745;
          color: white;
          padding: 15px 20px;
          border-radius: 10px;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
          z-index: 9999;
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          animation: slideIn 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 10px;
        ">
          <span>✅</span>
          <span>You have been signed out successfully!</span>
        </div>
        <style>
          @keyframes slideIn {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
        </style>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        notification.style.animation = 'slideIn 0.3s ease-out reverse';
        setTimeout(() => {
          document.body.removeChild(notification);
        }, 300);
      }, 3000);
    },
    
    loadDarkModePreference() {
      const darkMode = localStorage.getItem('ramyeon_dark_mode');
      this.isDarkMode = darkMode === 'true';
      
      // Apply dark mode class to document root
      if (this.isDarkMode) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
    },
    
    loadCartItems() {
      const savedCart = localStorage.getItem('ramyeon_cart');
      if (savedCart) {
        try {
          this.cartItems = JSON.parse(savedCart);
        } catch (error) {
          console.error('Error loading cart items:', error);
          this.cartItems = [];
        }
      } else {
        // Explicitly clear cartItems when localStorage is empty
        this.cartItems = [];
      }
    },
    
    // ---- Active Order Banner ----
    handleOrderPlaced(event) {
      const orderId = event.detail?.orderId;
      if (!orderId) return;
      this.activeOrder = { id: orderId, status: 'pending' };
      this.activeOrderMinimized = false;
      localStorage.setItem('ramyeon_active_order', JSON.stringify(this.activeOrder));
      this.startOrderPolling();
    },

    loadActiveOrder() {
      try {
        const stored = localStorage.getItem('ramyeon_active_order');
        if (!stored) return;
        const order = JSON.parse(stored);
        if (!['completed', 'delivered', 'cancelled'].includes(order.status)) {
          this.activeOrder = order;
          this.startOrderPolling();
        } else {
          localStorage.removeItem('ramyeon_active_order');
        }
      } catch { /* */ }
    },

    startOrderPolling() {
      clearInterval(this._orderPollTimer);
      this._orderPollTimer = setInterval(() => this.pollOrderStatus(), 30000);
    },

    async pollOrderStatus() {
      if (!this.activeOrder?.id) return;
      try {
        const result = await ordersAPI.getStatus(this.activeOrder.id);
        if (!result?.success) return;
        const newStatus = result.order_status || result.data?.order_status;
        if (!newStatus || newStatus === this.activeOrder.status) return;
        this.activeOrder = { ...this.activeOrder, status: newStatus };
        localStorage.setItem('ramyeon_active_order', JSON.stringify(this.activeOrder));
        if (['completed', 'delivered'].includes(newStatus)) {
          setTimeout(() => this.dismissOrderBanner(), 15000);
        } else if (newStatus === 'cancelled') {
          setTimeout(() => this.dismissOrderBanner(), 8000);
        }
      } catch { /* silent */ }
    },

    dismissOrderBanner() {
      this.activeOrder = null;
      this.activeOrderMinimized = false;
      localStorage.removeItem('ramyeon_active_order');
      clearInterval(this._orderPollTimer);
    },
    // ---- End Active Order Banner ----

    handleCartCleared() {
      this.cartItems = [];
      this.loadCartItems();
      this.$nextTick(() => this.$forceUpdate());
    }
  },
  
  watch: {
    // Watch for dark mode changes from settings
    isDarkMode(newVal) {
      if (newVal) {
        document.documentElement.classList.add('dark-mode');
      } else {
        document.documentElement.classList.remove('dark-mode');
      }
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');
@import './assets/EnhancedFooter.css';

/* User Menu Styles */
.user-menu {
  display: flex;
  align-items: center;
  gap: 15px;
}

.welcome-text {
  color: white;
  font-weight: 500;
  font-size: 0.9rem;
}

.profile-link,
.logout-link {
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  padding: 5px 10px;
  border-radius: 15px;
}

.profile-link:hover {
  background: rgba(255, 255, 255, 0.1);
  text-decoration: underline;
}

.logout-link:hover {
  background: rgba(255, 71, 87, 0.8);
  text-decoration: none;
}

/* Dark mode support for user menu */
.dark-mode .welcome-text {
  color: #f5f5f5;
}

.dark-mode .profile-link,
.dark-mode .logout-link {
  color: #f5f5f5;
}

/* Responsive design for user menu */
@media (max-width: 768px) {
  .user-menu {
    flex-direction: column;
    gap: 8px;
    align-items: flex-end;
  }
  
  .welcome-text {
    font-size: 0.8rem;
  }
  
  .profile-link,
  .logout-link {
    font-size: 0.8rem;
    padding: 3px 8px;
  }
}

@media (max-width: 480px) {
  .auth-social {
    flex-direction: column;
    gap: 10px;
    align-items: flex-end;
  }
  
  .user-menu {
    order: -1;
  }
  
  .social-icons {
    gap: 8px;
  }
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html, body {
  margin: 0;
  padding: 0;
  width: 100%;
  overflow-x: hidden;
}

#app {
  font-family: 'Poppins', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333;
  margin: 0;
  padding: 0;
  width: 100vw;
  overflow-x: hidden;
}

/* Top Bar - Enhanced Design */
.top-bar {
  background: linear-gradient(135deg, #2c3e50 0%, #3a3a3a 50%, #2c3e50 100%);
  color: white;
  padding: 12px 0;
  font-size: 0.9rem;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.top-bar-container {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.contact-info {
  display: flex;
  gap: 25px;
  align-items: center;
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.contact-item:hover {
  background: rgba(255, 255, 255, 0.15);
  transform: translateY(-1px);
}

.contact-icon {
  font-size: 1rem;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3));
}

.contact-text {
  font-weight: 500;
  font-size: 0.85rem;
  letter-spacing: 0.3px;
}

.auth-social {
  display: flex;
  align-items: center;
  gap: 20px;
}

.auth-links {
  display: flex;
  gap: 12px;
  align-items: center;
}

.auth-btn {
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  padding: 8px 16px;
  border-radius: 20px;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.login-btn {
  background: rgba(255, 255, 255, 0.1);
}

.login-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.1);
}

.signup-btn {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  border: 1px solid #ff4757;
}

.signup-btn:hover {
  background: linear-gradient(135deg, #ff3742, #ff2f3a);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
}

.social-icons {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-left: 20px;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.social-link:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.15);
}

.social-link img {
  width: 16px;
  height: 16px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.social-link:hover img {
  filter: brightness(0) invert(1) drop-shadow(0 2px 4px rgba(255, 255, 255, 0.3));
}

/* Top Bar Responsive Design */
@media (max-width: 992px) {
  .top-bar-container {
    padding: 0 15px;
  }
  
  .contact-info {
    gap: 15px;
  }
  
  .contact-item {
    padding: 5px 10px;
  }
  
  .contact-text {
    font-size: 0.8rem;
  }
  
  .auth-social {
    gap: 15px;
  }
  
  .social-icons {
    padding-left: 15px;
    gap: 10px;
  }
  
  .social-link {
    width: 28px;
    height: 28px;
  }
  
  .social-link img {
    width: 14px;
    height: 14px;
  }
}

@media (max-width: 768px) {
  .top-bar {
    padding: 10px 0;
  }
  
  .top-bar-container {
    flex-direction: column;
    gap: 12px;
    padding: 0 15px;
  }
  
  .contact-info {
    order: 2;
    gap: 12px;
    justify-content: center;
  }
  
  .contact-item {
    padding: 4px 8px;
    font-size: 0.75rem;
  }
  
  .contact-text {
    font-size: 0.75rem;
  }
  
  .auth-social {
    order: 1;
    gap: 12px;
    width: 100%;
    justify-content: space-between;
  }
  
  .auth-links {
    gap: 8px;
  }
  
  .auth-btn {
    padding: 6px 12px;
    font-size: 0.75rem;
  }
  
  .social-icons {
    padding-left: 0;
    border-left: none;
    gap: 8px;
  }
  
  .social-link {
    width: 26px;
    height: 26px;
  }
  
  .social-link img {
    width: 12px;
    height: 12px;
  }
}

@media (max-width: 480px) {
  .top-bar {
    padding: 8px 0;
  }
  
  .contact-info {
    flex-direction: column;
    gap: 8px;
  }
  
  .contact-item {
    padding: 3px 6px;
  }
  
  .contact-icon {
    font-size: 0.9rem;
  }
  
  .contact-text {
    font-size: 0.7rem;
  }
  
  .auth-social {
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  
  .auth-links {
    order: 1;
  }
  
  .social-icons {
    order: 2;
    gap: 6px;
  }
  
  .social-link {
    width: 24px;
    height: 24px;
  }
  
  .social-link img {
    width: 10px;
    height: 10px;
  }
}

/* Navigation Bar */
.navbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  background-color: white;
  box-shadow: 0 2px 4px rgb(0 0 0 / 0.1);
  font-weight: 600;
  font-size: 1rem;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
}

.navbar > div {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
}

.logo {
  height: 70px;
  display: flex;
  align-items: center;
}

.logo img {
  height: 100%;
  width: auto;
  object-fit: contain;
  max-width: 220px;
}

.nav-links {
  list-style: none;
  display: flex;
  gap: 25px;
  margin: 0;
  padding: 0;
}

.nav-links li a {
  color: black;
  text-decoration: none;
  padding: 5px 0;
  transition: color 0.3s ease;
}

.nav-links li a.active,
.nav-links li a:hover {
  color: red;
  font-weight: 700;
}

.cart-btn {
  background: none;
  border: none;
  font-size: 1.6rem;
  position: relative;
  cursor: pointer;
  color: #666;
}

.cart-count {
  position: absolute;
  top: -8px;
  right: -10px;
  background-color: red;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 50%;
  box-shadow: 0 0 8px red;
}

/* Footer Section - Ultra Modern Design */
.footer {
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #2d2d2d 100%);
  padding: 60px 0 0 0;
  margin-top: 80px;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  color: #f5f5f5;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.4);
  overflow: hidden;
}

.footer::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(255, 71, 87, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 71, 87, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 40% 80%, rgba(255, 71, 87, 0.06) 0%, transparent 50%);
  pointer-events: none;
  animation: gradientShift 20s ease-in-out infinite;
}

@keyframes gradientShift {
  0%, 100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.1) rotate(2deg);
  }
}

.footer-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 1;
}

.footer-main {
  display: grid;
  grid-template-columns: 1.5fr 1.8fr 1fr 1fr;
  gap: 50px;
  margin-bottom: 40px;
  align-items: start;
  padding-bottom: 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-brand {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
}

.footer-logo {
  height: 45px;
  width: auto;
  object-fit: contain;
  max-width: 180px;
  filter: brightness(0) invert(1);
  margin-bottom: 8px;
  transition: transform 0.3s ease;
}

.footer-logo:hover {
  transform: scale(1.05);
}

.footer-address {
  color: #b8b8b8;
  font-size: 0.9rem;
  line-height: 1.6;
  margin: 0;
  font-weight: 400;
}

.footer-contact {
  color: #d4d4d4;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.footer-newsletter {
  display: flex;
  flex-direction: column;
  gap: 15px;
  text-align: left;
}

.newsletter-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
}

.newsletter-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: rgba(255, 71, 87, 0.15);
  border-radius: 50%;
  color: #ff4757;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 71, 87, 0.2);
}

.newsletter-icon:hover {
  background: rgba(255, 71, 87, 0.25);
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.3);
}

.footer-newsletter h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.newsletter-form {
  display: flex;
  gap: 0;
  border-radius: 25px;
  overflow: hidden;
  background: #3a3a3a;
  border: 2px solid #4a4a4a;
  max-width: 320px;
  margin: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  position: relative;
}

.newsletter-form:hover {
  border-color: #ff4757;
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(255, 71, 87, 0.2);
}

.newsletter-form:focus-within {
  border-color: #ff4757;
  box-shadow: 0 6px 20px rgba(255, 71, 87, 0.3), 0 0 0 3px rgba(255, 71, 87, 0.1);
}

.newsletter-form.error {
  border-color: #e74c3c;
  animation: shake 0.5s ease-in-out;
}

.newsletter-form.success {
  border-color: #27ae60;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.input-container {
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
}

.newsletter-input {
  flex: 1;
  padding: 12px 18px 12px 45px;
  border: none;
  font-size: 0.9rem;
  outline: none;
  background: transparent;
  color: #ffffff;
  font-weight: 400;
  transition: all 0.3s ease;
}

.newsletter-input::placeholder {
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
}

.newsletter-input:focus {
  background: rgba(255, 255, 255, 0.05);
}

.newsletter-input:focus::placeholder {
  color: rgba(255, 255, 255, 0.4);
  transform: translateX(3px);
}

.newsletter-input.error {
  color: #ffcdd2;
}

.newsletter-input.success {
  color: #c8e6c9;
}

.input-icon {
  position: absolute;
  left: 15px;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
  pointer-events: none;
}

.newsletter-input:focus + .input-icon {
  color: #ff4757;
  transform: scale(1.1);
}

.newsletter-input.error + .input-icon {
  color: #e74c3c;
}

.newsletter-input.success + .input-icon {
  color: #27ae60;
}

.subscribe-btn {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
  border: none;
  padding: 12px 20px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
}

.subscribe-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.subscribe-btn:hover {
  background: linear-gradient(135deg, #ff3742, #ff2f3a);
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(255, 71, 87, 0.4);
}

.subscribe-btn:hover::before {
  left: 100%;
}

.subscribe-btn:active {
  transform: translateY(0);
}

.subscribe-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.subscribe-btn:disabled:hover {
  transform: none;
}

.loading-text svg {
  animation: spin 1s linear infinite;
}

.arrow-icon {
  transition: transform 0.3s ease;
}

.subscribe-btn:hover .arrow-icon {
  transform: translateX(2px);
}

.subscribe-btn:disabled .arrow-icon {
  display: none;
}

.form-feedback {
  min-height: 20px;
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.error-message {
  color: #ffcdd2;
  background: rgba(244, 67, 54, 0.15);
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin: 0;
  border: 1px solid rgba(244, 67, 54, 0.3);
  animation: slideInUp 0.3s ease-out;
}

.success-message {
  color: #c8e6c9;
  background: rgba(76, 175, 80, 0.15);
  padding: 6px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  margin: 0;
  border: 1px solid rgba(76, 175, 80, 0.3);
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 25px;
  align-items: flex-start;
}

.footer-column h4 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 15px 0;
  position: relative;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.footer-column h4::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 0;
  width: 40px;
  height: 3px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  border-radius: 2px;
}

.footer-column ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-column ul li a {
  color: #b8b8b8;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  font-weight: 400;
  position: relative;
  display: inline-block;
}

.footer-column ul li a::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  transition: width 0.3s ease;
}

.footer-column ul li a:hover {
  color: #ff4757;
  transform: translateX(5px);
}

.footer-column ul li a:hover::before {
  width: 100%;
}

.footer-social {
  display: flex;
  justify-content: center;
  gap: 10px;
  padding: 15px 0 5px 0;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  transition: all 0.3s ease;
  padding: 0;
  border: 2px solid transparent;
}

.social-link:hover {
  transform: translateY(-3px) scale(1.1);
  background: linear-gradient(135deg, #ff4757, #ff3742);
  border-color: rgba(255, 255, 255, 0.2);
  box-shadow: 0 6px 20px rgba(255, 71, 87, 0.3);
}

.social-icon-img {
  width: 20px;
  height: 20px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  transition: all 0.3s ease;
}

.social-link:hover .social-icon-img {
  filter: brightness(0) invert(1) drop-shadow(0 2px 4px rgba(255, 255, 255, 0.3));
  transform: scale(1.1);
}

.footer-bottom {
  background: linear-gradient(135deg, #0f0f0f, #1a1a1a);
  font-size: 0.8rem;
  padding: 5px 0;
  text-align: center;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-bottom-content p {
  color: #aaa;
  font-size: 0.9rem;
  margin: 0;
  letter-spacing: 0.8px;
  font-weight: 400;
}

.newsletter-disclaimer {
  font-size: 0.8rem;
  color: #b8b8b8;
  margin: 8px 0 0 0;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s ease;
}

.newsletter-disclaimer:hover {
  color: #d4d4d4;
  transform: translateY(-1px);
}

.newsletter-disclaimer svg {
  flex-shrink: 0;
  opacity: 0.8;
}

.email-policy-link {
  color: #ff4757;
  text-decoration: none;
  transition: all 0.3s ease;
  font-weight: 500;
}

.email-policy-link:hover {
  color: #ff3742;
  text-decoration: underline;
}

/* Enhanced Responsive Design */
@media (max-width: 992px) {
  .footer-main {
    grid-template-columns: 1fr 1fr;
    gap: 30px;
  }
  
  .footer-newsletter {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .footer {
    padding: 30px 0 0 0;
  }
  
  .footer-main {
    grid-template-columns: 1fr;
    gap: 25px;
    text-align: center;
  }
  
  .footer-brand {
    align-items: center;
  }
  
  .footer-newsletter {
    text-align: center;
    grid-column: span 1;
  }
  
  .newsletter-form {
    max-width: 100%;
  }
  
  .footer-column h4::after {
    left: 50%;
    transform: translateX(-50%);
  }
  
  .footer-social {
    margin-bottom: 10px;
    gap: 15px;
    padding: 10px 0 5px 0;
  }
}

@media (max-width: 480px) {
  .footer {
    padding: 25px 0 0 0;
  }
  
  .footer-content {
    padding: 0 20px;
  }
  
  .footer-main {
    gap: 20px;
  }
  
  .footer-logo {
    height: 35px;
    max-width: 150px;
  }
  
  .footer-newsletter h3 {
    font-size: 1.1rem;
  }
  
  .newsletter-form {
    flex-direction: column;
    border-radius: 15px;
  }
  
  .newsletter-input {
    border-bottom: 1px solid #4a4a4a;
  }
  
  .subscribe-btn {
    border-radius: 0 0 15px 15px;
  }
  
  .social-link {
    width: 40px;
    height: 40px;
  }
  
  .social-icon-img {
    width: 18px;
    height: 18px;
  }
}

/* ===== Active Order Banner ===== */
.order-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: calc(100% - 48px);
  max-width: 900px;
  padding: 12px 20px;
  display: flex;
  justify-content: center;
  z-index: 600;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
  animation: bannerSlideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
@keyframes bannerSlideUp {
  from { opacity: 0; transform: translateX(-50%) translateY(30px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* Light base with brand red outline */
.order-banner.banner-active,
.order-banner.banner-preparing,
.order-banner.banner-otw,
.order-banner.banner-done {
  background: #ffffff;
  border: 2.5px solid #ff4757;
}
.order-banner.banner-cancelled {
  background: #fff5f5;
  border: 2.5px solid #ffb3b3;
}

.order-banner-inner {
  max-width: 1200px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 20px;
  color: #1a1a1a;
  font-family: 'Poppins', sans-serif;
}

.order-banner-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.order-banner-info {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}
.order-banner-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #ff4757;
}
.order-banner.banner-cancelled .order-banner-label { color: #cc3333; }
.order-banner-id {
  font-size: 0.72rem;
  color: #888;
}

/* Pulsing dot */
.order-pulse-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  animation: pulse 1.6s ease-in-out infinite;
}
.order-pulse-dot.banner-active,
.order-pulse-dot.banner-preparing,
.order-pulse-dot.banner-otw    { background: #ff4757; box-shadow: 0 0 0 0 rgba(255,71,87,0.5); }
.order-pulse-dot.banner-done   { background: #ff4757; animation: none; }
.order-pulse-dot.banner-cancelled { background: #ffb3b3; animation: none; }
@keyframes pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255,71,87,0.5); }
  70%  { box-shadow: 0 0 0 8px rgba(255,71,87,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,71,87,0); }
}

/* Step progress */
.order-banner-steps {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}
.order-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 60px;
}
.step-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #ddd;
  transition: all 0.3s ease;
}
.order-step.completed .step-dot {
  background: #ff4757;
  border-color: #ff4757;
  box-shadow: 0 0 5px rgba(255,71,87,0.35);
}
.order-step.active .step-dot {
  background: #ff4757;
  border-color: #ff4757;
  box-shadow: 0 0 10px rgba(255,71,87,0.45);
  transform: scale(1.4);
}
.step-label {
  font-size: 0.62rem;
  color: #bbb;
  text-align: center;
  white-space: nowrap;
  transition: color 0.3s, font-weight 0.3s;
}
.order-step.active .step-label    { color: #ff4757; font-weight: 700; }
.order-step.completed .step-label { color: #aaa; }
.step-connector {
  flex: 1;
  height: 2px;
  background: #e8e8e8;
  min-width: 20px;
  max-width: 50px;
  margin-bottom: 14px;
  transition: background 0.3s ease;
}
.step-connector.completed { background: #ff4757; }

/* Actions */
.order-banner-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.order-view-btn {
  background: transparent;
  border: 2.5px solid #ff4757;
  color: #ff4757;
  padding: 5px 14px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
}
.order-view-btn:hover {
  background: #ff4757;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(255,71,87,0.3);
}
.order-banner-minimize,
.order-banner-close {
  background: none;
  border: none;
  color: #ccc;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 0.2s;
}
.order-banner-minimize:hover,
.order-banner-close:hover { color: #ff4757; }

/* Minimized pill — fixed bottom-right */
.order-banner-pill {
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: #ffffff;
  border: 2.5px solid #ff4757;
  color: #1a1a1a;
  border-radius: 30px;
  padding: 9px 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.78rem;
  font-weight: 600;
  font-family: 'Poppins', sans-serif;
  cursor: pointer;
  z-index: 600;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1), 0 1px 4px rgba(255,71,87,0.15);
  transition: transform 0.2s, box-shadow 0.2s;
  animation: bannerSlideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.order-banner-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0,0,0,0.12), 0 2px 8px rgba(255,71,87,0.25);
}
.pill-expand { font-size: 0.6rem; color: #ff4757; margin-left: 4px; }

/* Responsive */
@media (max-width: 768px) {
  .order-banner-steps { display: none; }
  .order-banner-inner { gap: 12px; }
  .order-banner { width: calc(100% - 32px); bottom: 16px; }
}
@media (max-width: 480px) {
  .order-banner { width: calc(100% - 24px); bottom: 12px; border-radius: 12px; }
}
@media (max-width: 480px) {
  .order-view-btn { display: none; }
}
/* ===== End Active Order Banner ===== */

/* Enhanced slide transitions for auth pages */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.slide-enter-from {
  opacity: 0;
  transform: translateX(100%) rotateY(30deg) scale(0.9);
  filter: blur(5px);
}

.slide-enter-to {
  opacity: 1;
  transform: translateX(0) rotateY(0deg) scale(1);
  filter: blur(0px);
}

.slide-leave-from {
  opacity: 1;
  transform: translateX(0) rotateY(0deg) scale(1);
  filter: blur(0px);
}

.slide-leave-to {
  opacity: 0;
  transform: translateX(-100%) rotateY(-30deg) scale(0.9);
  filter: blur(5px);
}

/* Additional animations and effects */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.footer-main > div {
  animation: fadeInUp 0.6s ease-out;
}

.footer-main > div:nth-child(1) { animation-delay: 0.1s; }
.footer-main > div:nth-child(2) { animation-delay: 0.2s; }
.footer-main > div:nth-child(3) { animation-delay: 0.3s; }
.footer-main > div:nth-child(4) { animation-delay: 0.4s; }

/* Sign Out Modal Styles */
.signout-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
  animation: fadeIn 0.3s ease-out;
}

.signout-modal {
  background: white;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 400px;
  width: 90%;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
}

.signout-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
}

.signout-modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
}

.signout-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  font-size: 1.2rem;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.signout-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.signout-modal-body {
  padding: 30px 25px;
  text-align: center;
}

.signout-icon {
  font-size: 3rem;
  margin-bottom: 15px;
  display: block;
}

.signout-message {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.signout-submessage {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
}

.signout-modal-footer {
  display: flex;
  gap: 15px;
  padding: 20px 25px;
  background: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.signout-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.cancel-btn {
  background: #6c757d;
  color: white;
}

.cancel-btn:hover {
  background: #5a6268;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(108, 117, 125, 0.3);
}

.confirm-btn {
  background: linear-gradient(135deg, #ff4757, #ff3742);
  color: white;
}

.confirm-btn:hover {
  background: linear-gradient(135deg, #ff3742, #ff2f3a);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);
}

.confirm-btn:active,
.cancel-btn:active {
  transform: translateY(0);
}

/* Dark mode support for modal */
.dark-mode .signout-modal {
  background: #2d2d2d;
  color: #f5f5f5;
}

.dark-mode .signout-modal-footer {
  background: #3a3a3a;
  border-top-color: #4a4a4a;
}

.dark-mode .signout-message {
  color: #f5f5f5;
}

.dark-mode .signout-submessage {
  color: #b8b8b8;
}

/* Modal animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive modal */
@media (max-width: 480px) {
  .signout-modal {
    margin: 20px;
    width: calc(100% - 40px);
  }

  .signout-modal-header {
    padding: 15px 20px;
  }

  .signout-modal-body {
    padding: 25px 20px;
  }

  .signout-modal-footer {
    padding: 15px 20px;
    flex-direction: column;
  }

  .signout-btn {
    width: 100%;
  }
}
</style>
